import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Bot,
  Check,
  ChevronRight,
  Clock3,
  Crown,
  Gem,
  Gift,
  Landmark,
  LogOut,
  Monitor,
  MonitorCog,
  Moon,
  Palette,
  PenLine,
  ReceiptText,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Store,
  Sun,
  User,
  Users,
  Wallet,
  Workflow,
} from 'lucide-react';
import api from '../../utils/api';
import paymentApi from '../../utils/paymentApi';
import './AccountCenterPage.css';
import ProfileSettingsPanel from './ProfileSettingsPanel';
import UserAvatar from '../components/UserAvatar';
import SecuritySettingsPanel from './SecuritySettingsPanel';
import SubscriptionPlansPanel from './SubscriptionPlansPanel';
import CreditStorePanel from './CreditStorePanel';
import CreditLedgerPanel from './CreditLedgerPanel';
import CreditOrderPanel from './CreditOrderPanel';
import BillingOrdersPanel from './BillingOrdersPanel';
import UsageDashboardPanel from './UsageDashboardPanel';
import { formatBillingDate, getBillingLogLabel } from './billingData';
import WritingSettingsPanel from './settings/WritingSettingsPanel';
import EditorSettingsPanel from './settings/EditorSettingsPanel';
import AgentSettingsPanel from './settings/AgentSettingsPanel';
import CanvasSettingsPanel from './settings/CanvasSettingsPanel';
import './settings/CreationSettings.css';
import TeamBenefitsPanel from './TeamBenefitsPanel';
import TeamSettingsPanel from './TeamSettingsPanel';
import RewardsCenterPanel from './RewardsCenterPanel';
import CorporateFinancePanel from './CorporateFinancePanel';
import { replaceBrandValue, useTeamBrand } from '../branding/TeamBrandContext';
import './GroupedAccountCenter.css';

// 在线开票入口可见；国信开票配置未完成时，服务端仍会拒绝开票且不会占用订单。
const ONLINE_INVOICE_VISIBLE = true;

function accountConcurrencyLabel(limit, teamScope, english) {
  const parsed = Number(limit);
  const normalized = Number.isInteger(parsed) && parsed >= 0 ? parsed : 2;
  if (english) {
    if (normalized === 0) return teamScope ? 'Unlimited shared team concurrency' : 'Unlimited personal concurrency';
    return teamScope ? `${normalized} shared team tasks` : `${normalized} personal tasks`;
  }
  if (normalized === 0) return teamScope ? '团队共享不限并发' : '个人不限并发';
  return teamScope ? `团队共享 ${normalized} 个并发任务` : `个人 ${normalized} 个并发任务`;
}

const ACCOUNT_ITEMS_EN = {
  '/account/profile': ['Profile', 'Manage your identity, credit balance, and account status.'],
  '/account/security': ['Sign-in & Security', 'Manage WeChat, phone, email binding, and signed-in devices.', ['WeChat QR binding', 'Phone and email verification', 'Device and session management']],
  '/billing/plan': ['Subscription Plans', 'Compare plan benefits, monthly credits, renewal, and upgrades.', ['Creator and Pro benefits', 'Monthly credits and concurrency', 'Upgrade, renew, or cancel']],
  '/billing/store': ['Credit Packs', 'Purchase one-time credit packs from the server-managed product catalog.', ['One-time credit packs', 'Expiration and bonus rules', 'Personal or team wallet']],
  '/billing/credits': ['Credit Account', 'View balances, activity, subscriptions, and one-time credit purchase status.'],
  '/team': ['Team Benefits', 'Team seats, shared projects and assets, shared credits, and member limits.', ['Team seats and roles', 'Shared projects and assets', 'Shared credits and member limits']],
  '/billing/rewards': ['Rewards', 'Redeem a code for credit rewards and review recent results.', ['Redeem credit codes', 'Result and failure details', 'Instant credit delivery']],
  '/billing/orders': ['Billing History', 'Review real orders, payment attempts, refunds, and wallet delivery.'],
  '/billing/finance': ['Online Invoices', 'Apply for e-invoices from completed online payment orders.', ['Electronic invoice applications', 'Completed order selection', 'Invoice file delivery']],
  '/billing/usage': ['Usage Dashboard', 'Start with real credit activity; project and model breakdowns will follow.', ['Writing and generation usage', 'Project and date filters', 'Team member usage alerts']],
  '/settings/general': ['General', 'Theme, language, text size, motion, and notification preferences.', ['Warm light and warm dark themes', 'Interface and reading size', 'Motion and notifications']],
  '/settings/writing': ['Writing', 'Tune saving, creative rhythm, and version habits to fit your workflow.', ['Autosave and delay', 'Workflow depth and Plan Mode', 'Automatic snapshots and recovery']],
  '/settings/editor': ['Editor', 'Control reading and editing without changing script content.', ['Font, size, and line height', 'Paper and eye-comfort backgrounds', 'Dialogue highlighting and focus mode']],
  '/settings/agent': ['Agent', 'Choose the Agent layout, run-detail level, and confirmation rules.', ['Collapsed, floating, or maximized', 'Brief or detailed run tracking', 'Paid and high-risk action confirmation']],
  '/settings/canvas': ['Canvas', 'Set canvas interaction and image, video, and audio defaults.', ['Grid snapping and performance mode', 'Default ratios and authorized models', 'Cost estimates before generation']],
  '/settings/credit-order': ['Credit Order', 'Choose the preferred wallet and reorder personal credit batches.', ['Personal or team priority', 'Drag personal credit batches', 'New credits append automatically']],
  '/team/settings': ['Team Settings', 'Manage workspace details, default permissions, model access, and billing contacts.', ['Workspace profile', 'Model and review permissions', 'Billing contacts and sensitive actions']],
  '/learn': ['Tutorials', 'Explore official Xiaoying tutorials and examples.'],
  '/help/agent': ['Agent Guide', 'Learn to use Agents for writing, review, and canvas production.', ['Getting started with the writing Agent', 'Consistency and candidate drafts', 'Canvas Agent workflows']],
};

function localizedAccountItem(item, english) {
  if (!english) return item;
  const translation = ACCOUNT_ITEMS_EN[item.path];
  if (!translation) return item;
  return {
    ...item,
    label: translation[0],
    description: translation[1],
    features: translation[2] || item.features,
  };
}

export const ACCOUNT_GROUPS = [
  {
    label: '账户',
    items: [
      {
        path: '/account/profile',
        label: '个人资料',
        icon: User,
        status: 'available',
        description: '管理身份资料，查看真实点数和账户状态。',
      },
      {
        path: '/account/security',
        label: '登录与安全',
        icon: ShieldCheck,
        status: 'available',
        description: '微信、手机号、邮箱绑定以及登录设备管理。',
        features: ['微信扫码绑定', '手机号与邮箱验证码绑定', '登录设备与会话管理'],
      },
    ],
  },
  {
    label: '订阅和充值',
    items: [
      {
        path: '/billing/plan',
        label: '订阅套餐',
        icon: Crown,
        status: 'available',
        description: '查看套餐权益、月度点数、续费和升级方案。',
        features: ['创作者与专业版权益', '月度点数和并发能力', '升级、续费与取消'],
      },
      {
        path: '/billing/store',
        label: '点数套餐',
        icon: Store,
        status: 'available',
        description: '从服务端商品目录购买一次性点数套餐，支付渠道按当前开放状态展示。',
        features: ['后台动态套餐与标签', '1–9份结算', '个人钱包到账'],
      },
      {
        path: '/billing/credits',
        label: '点数账户',
        icon: Gem,
        status: 'available',
        description: '查看当前余额、点数流水、有效订阅和点数直购状态。',
      },
    ],
  },
  {
    label: '权益和账单',
    items: [
      {
        path: '/team',
        label: '团队权益',
        icon: Users,
        status: 'available',
        description: '查看真实团队余额、套餐版本、团队ID和成员数量。',
        features: ['团队席位和角色', '共享项目与资产', '共享点数与成员额度'],
      },
      {
        path: '/billing/rewards',
        label: '奖励中心',
        icon: Gift,
        status: 'available',
        description: '使用兑换码领取积分奖励，并查看最近兑换结果。',
        features: ['兑换码领取积分', '兑换状态与失败原因', '奖励积分即时到账'],
      },
      {
        path: '/billing/orders',
        label: '账单记录',
        icon: ReceiptText,
        status: 'available',
        description: '查看真实订单、支付尝试、退款状态和点数到账。',
      },
      {
        path: '/billing/finance',
        label: '在线开票',
        icon: Landmark,
        status: 'available',
        description: '按已完成的线上支付订单自动开具电子发票。',
        features: ['数电普通发票', '已支付订单选择', '电子发票自动交付'],
      },
      {
        path: '/billing/usage',
        label: '用量看板',
        icon: BarChart3,
        description: '按点数流水查看创作能力用量。',
        features: ['写作与生成用量', '项目和时间筛选', '团队成员额度预警'],
      },
    ],
  },
  {
    label: '创作设置',
    items: [
      {
        path: '/settings/general',
        label: '通用设置',
        icon: Palette,
        status: 'available',
        description: '主题、语言、字号、动效和通知偏好。',
        features: ['暖白与暖黑主题', '界面与阅读字号', '动效和通知偏好'],
      },
      {
        path: '/settings/writing',
        label: '写作设置',
        icon: PenLine,
        status: 'available',
        description: '让保存、创作节奏和版本习惯更合你的手感。',
        features: ['自动保存与延迟', '工作流强度与 Plan Mode', '自动版本快照与恢复'],
      },
      {
        path: '/settings/editor',
        label: '编辑器设置',
        icon: SlidersHorizontal,
        status: 'available',
        description: '控制正文阅读和编辑体验，不改变剧本内容。',
        features: ['字体、字号与行高', '纸张与护眼背景', '对话高亮和专注模式'],
      },
      {
        path: '/settings/agent',
        label: 'Agent 设置',
        icon: Bot,
        status: 'available',
        description: '设置 Agent 默认形态、运行信息密度和高成本确认。',
        features: ['收起、浮层或最大化', '简洁或详细运行追踪', '付费和高风险动作确认'],
      },
      {
        path: '/settings/canvas',
        label: '画布设置',
        icon: Workflow,
        status: 'available',
        description: '设置画布交互和图片、视频、音频生成默认值。',
        features: ['网格吸附与性能模式', '默认比例和授权模型', '生成前费用提示'],
      },
      {
        path: '/settings/credit-order',
        label: '积分消耗设置',
        icon: Wallet,
        status: 'available',
        description: '选择优先消耗个人或团队积分，并调整个人积分批次顺序。',
        features: ['个人或团队优先', '拖拽个人积分批次', '新积分自动追加'],
      },
    ],
  },
  {
    label: '团队设置',
    items: [
      {
        path: '/team/settings',
        label: '团队设置',
        icon: MonitorCog,
        status: 'available',
        description: '在一个列表中管理团队、成员、角色、邀请和成员额度。',
        features: ['团队资料与成员', '邀请和角色', '成员额度与危险操作'],
      },
    ],
  },
  {
    label: '帮助与支持',
    items: [
      {
        path: '/learn',
        label: '使用教程',
        icon: BookOpen,
        status: 'available',
        description: '查看 Xiaoying 官方教程和案例。',
        external: true,
      },
      {
        path: '/help/agent',
        label: 'Agent 教程',
        icon: Sparkles,
        status: 'available',
        description: '学习如何用 Agent 完成写作、审核和画布生产。',
        features: ['写作 Agent 上手', '一致性与候选稿处理', '画布 Agent 工作流'],
      },
    ],
  },
];

const FLAT_ITEMS = ACCOUNT_GROUPS.flatMap(group => group.items);

const ACCOUNT_SECTIONS = [
  { id: 'account', label: '账户', labelEn: 'Account', icon: User, description: '个人资料、登录与安全', descriptionEn: 'Profile and security', paths: ['/account/profile', '/account/security'] },
  { id: 'creation', label: '创作偏好', labelEn: 'Creation', icon: SlidersHorizontal, description: '外观、写作与画布', descriptionEn: 'Appearance and workspace', paths: ['/settings/general', '/settings/writing', '/settings/editor', '/settings/agent', '/settings/canvas'] },
  { id: 'billing', label: '点数与账单', labelEn: 'Credits & Billing', icon: Wallet, description: '余额、套餐、用量与开票', descriptionEn: 'Balance, plans and invoices', paths: ['/billing/credits', '/billing/plan', '/billing/store', '/billing/usage', '/billing/orders', '/billing/finance', '/billing/rewards', '/settings/credit-order'] },
  { id: 'team', label: '团队', labelEn: 'Team', icon: Users, description: '团队权益与成员管理', descriptionEn: 'Benefits and members', paths: ['/team', '/team/settings'] },
];

const accountSection = path => ACCOUNT_SECTIONS.find(section => section.paths.includes(path)) || ACCOUNT_SECTIONS[0];

function normalizePath(pathname) {
  const normalized = pathname.replace(/^\/prototype\/vnext/, '') || '/account/profile';
  return ['/team/members', '/team/usage'].includes(normalized) ? '/team/settings' : normalized;
}

function AccountNavigation({ currentPath, preview, onSignOut, english, overlay }) {
  const brand = useTeamBrand();
  const [query, setQuery] = useState('');
  useEffect(() => setQuery(''), [currentPath]);
  const resolvePath = path => preview ? `/prototype/vnext${path}` : path;
  const selectedSection = accountSection(currentPath);
  const matches = query.trim() ? FLAT_ITEMS.filter(source => {
    const item = localizedAccountItem(source, english);
    return [item.label, item.description, ...(item.features || [])].join(' ').toLowerCase().includes(query.trim().toLowerCase());
  }) : [];

  return (
    <aside className="account-nav" aria-label={english ? 'Account center navigation' : '账户中心导航'}>
      <div className="account-nav__title">
        <span>{english ? 'Account Center' : '账户中心'}</span>
      </div>
      <label className="account-nav__search"><Search size={14} /><input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder={english ? 'Find a setting' : '搜索设置'} aria-label={english ? 'Search account and settings' : '搜索账户与设置'} /></label>
      <div className="account-nav__groups">
        {query.trim() ? <div className="account-nav__results">
          <span role="status">{english ? `${matches.length} results` : `找到 ${matches.length} 项`}</span>
          {matches.map(source => {
            const item = replaceBrandValue(localizedAccountItem(source, english), brand.active ? brand.name : '');
            const Icon = item.icon;
            return <NavLink key={item.path} to={resolvePath(item.path)} replace={overlay} className="account-nav__item"><Icon size={15} /><span>{item.label}</span></NavLink>;
          })}
        </div> : <nav className="account-nav__sections" aria-label={english ? 'Settings categories' : '设置分类'}>
          {ACCOUNT_SECTIONS.map(section => {
            const Icon = section.icon;
            const active = selectedSection.id === section.id;
            return <NavLink key={section.id} to={resolvePath(active ? currentPath : section.paths[0])} replace={overlay}
              className={`account-nav__section${active ? ' is-active' : ''}`} aria-current={active ? 'true' : undefined}>
              <Icon size={17} /><span><strong>{english ? section.labelEn : section.label}</strong><small>{english ? section.descriptionEn : section.description}</small></span><ChevronRight size={13} />
            </NavLink>;
          })}
        </nav>}
      </div>
      <div className="account-nav__actions"><div className="account-nav__support">
        <NavLink to={resolvePath('/learn')}><BookOpen size={14} />{english ? 'Tutorials' : '使用教程'}</NavLink>
        <NavLink to={resolvePath('/help/agent')}><Sparkles size={14} />{english ? 'Agent Guide' : 'Agent 教程'}</NavLink>
      </div>
      <button className="account-nav__logout" type="button" onClick={onSignOut}>
        <LogOut size={16} />
        <span>{english ? (preview ? 'Leave preview' : 'Sign out') : (preview ? '退出预览' : '退出账号')}</span>
      </button>
      </div>
    </aside>
  );
}

function AccountSectionNavigation({ currentPath, preview, english, overlay }) {
  return <nav className="account-section-nav" aria-label={english ? 'Section pages' : '分类内页面'}>
    {accountSection(currentPath).paths.map(path => {
      const item = localizedAccountItem(FLAT_ITEMS.find(candidate => candidate.path === path), english);
      return <NavLink key={path} to={preview ? `/prototype/vnext${path}` : path} replace={overlay}
        aria-current={path === currentPath ? 'page' : undefined} className={path === currentPath ? 'is-active' : ''}>{item.label}</NavLink>;
    })}
  </nav>;
}

function ProfileOverview({ user, account, logs, logLoading, logError, hideIdentity = false, english = false }) {
  const brand = useTeamBrand();
  const credits = Number(user?.credits || 0);
  const subscription = account?.subscriptions?.personal;
  const teamWallet = account?.team_wallet;
  const teamSubscription = account?.subscriptions?.team;
  const personalConcurrency = account?.concurrency?.personal;
  const teamConcurrency = account?.concurrency?.team;
  const initial = user?.username?.trim()?.[0]?.toUpperCase() || '影';

  return (
    <div className="account-content__stack">
      {!hideIdentity && <section className="account-profile-card">
        <UserAvatar user={user} fallback={initial} className="account-profile-card__avatar" />
        <div className="account-profile-card__identity">
          <span className="account-eyebrow">{english ? 'PERSONAL ACCOUNT' : '个人账户'}</span>
          <h1>{user?.username || brand.text(english ? 'Xiaoying Creator' : '小影创作者')}</h1>
          {user?.id && <p>{`${english ? 'User ID' : '用户 ID'} · ${user.id}`}</p>}
        </div>
      </section>}

      <div className="account-summary-grid">
        <section className="account-summary-card account-summary-card--credits">
          <div className="account-summary-card__top">
            <span className="account-summary-card__icon"><Gem size={18} /></span>
            <span className="account-status account-status--ready">{english ? 'Live balance' : '当前余额'}</span>
          </div>
          <p>{english ? 'Available credits' : '可用点数'}</p>
          <strong>{credits.toLocaleString(english ? 'en-US' : 'zh-CN')}</strong>
          <div className="account-credit-split"><span>{english ? 'Uses the earliest-expiring credits first' : '按最早到期批次优先消费'}</span></div>
        </section>

        <section className="account-summary-card">
          <div className="account-summary-card__top">
            <span className="account-summary-card__icon"><Crown size={18} /></span>
            <span className={`account-status ${subscription ? 'account-status--ready' : 'account-status--neutral'}`}>{subscription ? (english ? 'Active' : '生效中') : (english ? 'None' : '未订阅')}</span>
          </div>
          <p>{english ? 'Subscription plan' : '订阅套餐'}</p>
          <strong className="account-summary-card__text-value">{subscription?.plan_name || (english ? 'No active plan' : '暂无有效套餐')}</strong>
          <span className="account-summary-card__caption">{accountConcurrencyLabel(personalConcurrency?.limit ?? subscription?.concurrency_limit ?? 2, false, english)} · {subscription ? `${english ? 'Expires' : '到期'} ${new Date(subscription.expires_at).toLocaleString(english ? 'en-US' : 'zh-CN')}` : (english ? 'Free account' : '免费账户')}</span>
        </section>

        <section className="account-summary-card">
          <div className="account-summary-card__top">
            <span className="account-summary-card__icon"><Users size={18} /></span>
            <span className={`account-status ${teamWallet ? 'account-status--ready' : 'account-status--neutral'}`}>{teamWallet ? (english ? 'Current' : '当前团队') : (english ? 'None' : '未选择')}</span>
          </div>
          <p>{english ? 'Team workspace' : '团队工作区'}</p>
          <strong className="account-summary-card__text-value">{teamWallet ? `${teamWallet.team_name} · ${Number(teamWallet.credits || 0).toLocaleString(english ? 'en-US' : 'zh-CN')}` : (english ? 'No current team' : '暂无当前团队')}</strong>
          <span className="account-summary-card__caption">{teamWallet
            ? `${teamSubscription?.plan_name || (english ? 'No active team subscription' : '团队暂无有效订阅')} · ${accountConcurrencyLabel(teamConcurrency?.limit ?? teamSubscription?.concurrency_limit ?? 2, true, english)}`
            : (english ? 'Switch to a team to view its balance and subscription.' : '切换团队后可查看对应余额与订阅')}</span>
        </section>
      </div>

      <section className="account-activity">
        <div className="account-section-heading">
          <div>
            <span className="account-eyebrow">RECENT ACTIVITY</span>
            <h2>{english ? 'Recent credit activity' : '最近点数变动'}</h2>
          </div>
        </div>
        {logLoading ? (
          <div className="account-activity__empty">{english ? 'Loading credit activity…' : '正在读取点数流水…'}</div>
        ) : logError ? (
          <div className="account-activity__empty">{'点数流水暂时无法加载，账户其他功能仍可使用。'}</div>
        ) : logs.length === 0 ? (
          <div className="account-activity__empty">{english ? 'No credit activity yet' : '暂无点数变动'}</div>
        ) : (
          <div className="account-activity__list">
            {logs.slice(0, 5).map(item => {
              const positive = Number(item.amount) > 0;
              return (
                <div className="account-activity__row" key={item.id}>
                  <span className={`account-activity__dot${positive ? ' is-positive' : ''}`} />
                  <div>
                    <strong>{getBillingLogLabel(item, english ? 'en-US' : 'zh-CN')}</strong>
                    <span>{formatBillingDate(item.created_at, { locale: english ? 'en-US' : 'zh-CN' })}</span>
                  </div>
                  <b className={positive ? 'is-positive' : ''}>
                    {positive ? '+' : ''}{Number(item.amount || 0).toLocaleString(english ? 'en-US' : 'zh-CN')} {english ? 'credits' : '点'}
                  </b>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function StatusPanel({ item }) {
  const Icon = item.icon || Clock3;

  return (
    <section className="account-coming-soon">
      <div className="account-coming-soon__visual" aria-hidden="true">
        <div className="account-coming-soon__orb account-coming-soon__orb--one" />
        <div className="account-coming-soon__orb account-coming-soon__orb--two" />
        <span><Icon size={30} strokeWidth={1.45} /></span>
      </div>
      <div className="account-coming-soon__body">
        <h1>{item.label}</h1>
        <p>{item.description}</p>
        {item.features?.length > 0 && (
          <ul>
            {item.features.map(feature => (
              <li key={feature}><Check size={14} />{feature}</li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

const THEME_CHOICES = [
  {
    value: 'light',
    label: '浅色',
    description: '首页同款暖白',
    icon: Sun,
  },
  {
    value: 'dark',
    label: '深色',
    description: '暖黑专业工作台',
    icon: Moon,
  },
  {
    value: 'system',
    label: '跟随系统',
    description: '随设备自动切换',
    icon: Monitor,
  },
];

function GeneralSettings({ theme, setTheme, language, setLanguage, english }) {
  return (
    <div className="account-content__stack">
      <section className="account-page-heading">
        <div>
          <span className="account-eyebrow">GENERAL</span>
          <h1>{english ? 'General Settings' : '通用设置'}</h1>
          <p>{english ? 'Warm light is the default. You can also choose dark mode or follow your system.' : '默认使用首页的暖白配色，也可以切换深色或跟随系统。'}</p>
        </div>
      </section>

      <section className="account-settings-card">
        <div className="account-settings-card__heading">
          <div>
            <h2>{english ? 'Appearance' : '外观主题'}</h2>
            <p>{english ? 'Changes apply immediately to Home, Account Center, and the new workspace.' : '切换后立即作用于首页、账户中心和新版工作台。'}</p>
          </div>
          <span>{english ? 'Auto-saved' : '自动保存'}</span>
        </div>
        <div className="account-theme-options" role="radiogroup" aria-label={english ? 'Appearance theme' : '外观主题'}>
          {THEME_CHOICES.map(choice => {
            const Icon = choice.icon;
            const selected = theme === choice.value;
            return (
              <button
                type="button"
                role="radio"
                aria-checked={selected}
                className={`account-theme-option${selected ? ' is-selected' : ''}`}
                key={choice.value}
                onClick={() => setTheme?.(choice.value)}
              >
                <span className={`account-theme-option__preview account-theme-option__preview--${choice.value}`}>
                  <i />
                  <b />
                  <em />
                </span>
                <span className="account-theme-option__label">
                  <Icon size={15} />
                  <span>
                    <strong>{english ? ({ light: 'Light', dark: 'Dark', system: 'System' }[choice.value]) : choice.label}</strong>
                    <small>{english ? ({ light: 'Warm white, like Home', dark: 'Warm dark professional workspace', system: 'Follow your device' }[choice.value]) : choice.description}</small>
                  </span>
                  <i>{selected && <Check size={12} />}</i>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="account-settings-card">
        <div className="account-settings-card__heading">
          <div>
            <h2>{english ? 'More preferences' : '更多通用偏好'}</h2>
          </div>
        </div>
        <div className="account-settings-rows">
          {[
            [english ? 'Interface language' : '界面语言', english ? 'English' : '简体中文', true],
            [english ? 'Interface text size' : '界面字号', english ? 'Standard · Adjust with browser zoom' : '标准 · 可通过浏览器缩放调整'],
            [english ? 'Interface motion' : '界面动效', english ? 'Follows your device’s Reduce Motion setting' : '跟随设备的“减少动态效果”设置'],
          ].map(([label, value, languageRow]) => (
            <div className="account-settings-row" key={label}>
              <span>{label}</span>
              {languageRow ? (
                <button type="button" onClick={() => setLanguage?.(language === 'en-US' ? 'zh-CN' : 'en-US')}>
                  {value}<ChevronRight size={13} />
                </button>
              ) : <span>{value}</span>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default function AccountCenterPage() {
  const brand = useTeamBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const { user, refreshUser, preview = false, accountOverlay = false, theme = 'light', setTheme, language = 'zh-CN', setLanguage } = outletContext;
  const userId = user?.id;
  const english = language === 'en-US';
  const currentPath = normalizePath(location.pathname);
  const contentRef = useRef(null);
  useEffect(() => { if (contentRef.current) contentRef.current.scrollTop = 0; }, [currentPath]);
  const searchParams = new URLSearchParams(location.search);
  const requestedTeamCatalog = searchParams.get('type') === 'team';
  const isCreditAccountPath = ['/billing/credits', '/billing/packages'].includes(currentPath);
  const navigationPath = isCreditAccountPath ? '/billing/credits' : currentPath;
  const activeItem = useMemo(
    () => replaceBrandValue(
      localizedAccountItem(FLAT_ITEMS.find(item => item.path === navigationPath) || FLAT_ITEMS[0], english),
      brand.active ? brand.name : '',
    ),
    [brand.active, brand.name, navigationPath, english],
  );
  const [logs, setLogs] = useState([]);
  const [creditAccount, setCreditAccount] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState(false);
  const [accountRefresh, setAccountRefresh] = useState(0);

  useEffect(() => {
    if (
      preview
      || !userId
      || !['/account/profile', '/billing/credits', '/billing/packages'].includes(currentPath)
    ) return;
    let active = true;
    setLogLoading(true);
    setLogError(false);
    setCreditAccount(null);
    paymentApi.get('/credit-account', { params: { view: 'operations' } })
      .then(response => {
        const body = response?.data;
        if (Number(body?.code) !== 200) {
          throw new Error(body?.message || 'CREDIT_ACCOUNT_FAILED');
        }
        const account = body?.data && typeof body.data === 'object' ? body.data : {};
        if (active) {
          setCreditAccount(account);
          setLogs(Array.isArray(account?.ledger?.list) ? account.ledger.list : []);
        }
      })
      .catch(() => {
        if (active) setLogError(true);
      })
      .finally(() => {
        if (active) setLogLoading(false);
      });
    return () => { active = false; };
  }, [currentPath, preview, userId, accountRefresh]);

  const signOut = async () => {
    if (preview) {
      navigate('/home');
      return;
    }
    try {
      await api.post('/auth/logout');
    } catch {
      // 即使网络异常也应完成本机退出；服务端会话最迟随 JWT 到期。
    }
    localStorage.removeItem('token');
    window.dispatchEvent(new Event('auth-changed'));
    navigate('/home', { replace: true });
  };

  return (
    <div className={`account-center account-center--grouped${accountOverlay ? ' account-center--overlay' : ''}`}>
      <div className="account-center__ambient" aria-hidden="true" />
      <div className="account-center__shell">
        <AccountNavigation
          currentPath={navigationPath}
          preview={preview}
          onSignOut={signOut}
          english={english}
          overlay={accountOverlay}
        />
        <main className="account-content" ref={contentRef}>
          <AccountSectionNavigation currentPath={navigationPath} preview={preview} english={english} overlay={accountOverlay} />
          {!accountOverlay && <div className="account-content__topline">
            <span>{brand.text(english ? 'Xiaoying Creative Account' : '小影创作账户')}</span>
            <span>{english ? (preview ? 'UI Preview' : 'Secure Connection') : (preview ? '界面预览' : '安全连接')}</span>
          </div>}
          {preview && (
            <section className="account-preview-notice" aria-label={english ? 'Preview information' : '预览说明'}>
              <span>{['/billing/plan', '/billing/store'].includes(currentPath)
                ? (english ? 'Purchases on this preview page create actual orders. Confirm your account before paying.' : '此预览页的购买操作会创建实际订单，请确认账户后再付款。')
                : (english ? 'Profile changes, exports, and invoicing are unavailable in preview. Open the account page to use them with your account permissions.' : '预览中无法修改账户资料、导出或开票。进入正式页面后，可按账号权限使用这些功能。')}</span>
              <NavLink to={`${currentPath}${location.search || ''}`}>{english ? 'Open account page' : '进入正式页面'}<ArrowRight size={13} /></NavLink>
            </section>
          )}
          {currentPath === '/account/profile' ? (
            <div className="account-content__stack">
              <ProfileSettingsPanel user={user} refreshUser={refreshUser} preview={preview} english={english} />
              <details className="account-profile-overview">
                <summary>{english ? 'Account overview and recent activity' : '账户概览与最近点数变动'}<ChevronRight size={14} /></summary>
                <ProfileOverview
                user={user}
                account={creditAccount}
                logs={logs}
                logLoading={logLoading}
                logError={logError}
                hideIdentity
                english={english}
                />
              </details>
            </div>
          ) : currentPath === '/account/security' ? (
            <div className="account-content__stack">
              <SecuritySettingsPanel
                user={user}
                refreshUser={refreshUser}
                preview={preview}
                english={english}
                darkActive={theme === 'dark'}
              />
            </div>
          ) : isCreditAccountPath ? (
            <CreditLedgerPanel
              onRetry={() => setAccountRefresh(value => value + 1)}
              user={user}
              account={creditAccount}
              logs={logs}
              loading={logLoading}
              error={logError}
              preview={preview}
              english={english}
            />
          ) : currentPath === '/billing/plan' ? (
            <div className="account-content__stack">
              <SubscriptionPlansPanel user={user} english={english} planType={requestedTeamCatalog ? 2 : 1} />
            </div>
          ) : currentPath === '/billing/store' ? (
            <div className="account-content__stack">
              <CreditStorePanel user={user} english={english} initialPlanType={requestedTeamCatalog ? 2 : 1} onViewOrders={() => navigate(preview ? '/prototype/vnext/billing/orders' : '/billing/orders', { replace: accountOverlay })} />
            </div>
          ) : currentPath === '/billing/rewards' ? (
            <RewardsCenterPanel preview={preview} english={english} onRedeemed={refreshUser} />
          ) : currentPath === '/billing/orders' ? (
            <BillingOrdersPanel
              preview={preview}
              english={english}
            />
          ) : ONLINE_INVOICE_VISIBLE && currentPath === '/billing/finance' ? (
            <CorporateFinancePanel user={user} preview={preview} english={english} />
          ) : currentPath === '/billing/usage' ? (
            <UsageDashboardPanel
              user={user}
              preview={preview}
              english={english}
            />
          ) : currentPath === '/settings/general' ? (
            <GeneralSettings theme={theme} setTheme={setTheme} language={language} setLanguage={setLanguage} english={english} />
          ) : currentPath === '/settings/writing' ? (
            <WritingSettingsPanel preview={preview} english={english} />
          ) : currentPath === '/settings/editor' ? (
            <EditorSettingsPanel preview={preview} english={english} />
          ) : currentPath === '/settings/agent' ? (
            <AgentSettingsPanel preview={preview} english={english} />
          ) : currentPath === '/settings/canvas' ? (
            <CanvasSettingsPanel preview={preview} english={english} />
          ) : currentPath === '/settings/credit-order' ? (
            <CreditOrderPanel
              userId={user?.id}
              preview={preview}
              english={english}
            />
          ) : currentPath === '/team' ? (
            <TeamBenefitsPanel preview={preview} english={english} />
          ) : currentPath === '/team/settings' ? (
            <TeamSettingsPanel preview={preview} english={english} />
          ) : (
            <StatusPanel item={activeItem} english={english} />
          )}
          {!accountOverlay && <footer className="account-content__footer">
            <span>{english ? 'Important actions are explained before you confirm.' : '重要操作都会先说明，再由你确认'}</span>
            <NavLink to={preview ? '/prototype/vnext' : '/home'}>
              {english ? 'Back to Home' : '返回首页'} <ArrowRight size={13} />
            </NavLink>
          </footer>}
        </main>
      </div>
    </div>
  );
}
